import React from 'react'
import { observer } from 'mobx-react'

import { TableCell, TableRow } from 'components/Table/ClickableTable'
import BookkeepingManagerViewModel from 'admin/bookkeeping/BookkeepingManagerViewModel'
import { ColumnId } from 'admin/bookkeeping/BookkeepingManagerTableColumn'
import BookkeepingPendingAction, {
  BookkeepingPendingActionPlatformConnection,
} from 'admin/bookkeeping/BookkeepingPendingAction'
import { ImageCellContent } from 'components/Table/TableCell'
import Pill from 'components/Pill'
import { timeago } from 'utils/date-util'
import Member from 'iam/models/Member'

import styles from './BookkeepingManagerTableRow.sass'

interface BookkeepingManagerTableRowProps {
  bookkeepingPendingAction: BookkeepingPendingAction
  vm: BookkeepingManagerViewModel
}

export default observer(function BookkeepingManagerTableRow({
  bookkeepingPendingAction,
  vm,
}: BookkeepingManagerTableRowProps) {
  return (
    <TableRow
      className={styles.root}
      onClick={() =>
        vm.selectBookkeepingPendingAction(bookkeepingPendingAction)
      }
      active={
        vm.detailsVM.bookkeepingPendingAction.id === bookkeepingPendingAction.id
      }
    >
      {vm.columns.map((column) => {
        switch (column.id) {
          case ColumnId.JobId:
            return (
              <TextCell
                key={column.id}
                content={bookkeepingPendingAction.jobShortId}
              />
            )

          case ColumnId.Customer:
            return (
              <ImageCell
                key={column.id}
                title={bookkeepingPendingAction.customer?.displayName}
                image={bookkeepingPendingAction.customer?.avatar}
              />
            )

          case ColumnId.JobName:
            return (
              <TextCell
                key={column.id}
                content={bookkeepingPendingAction.jobName}
              />
            )
          case ColumnId.CompanyName:
            return (
              <CompanyNameCell
                key={column.id}
                platformConnection={bookkeepingPendingAction.platformConnection}
              />
            )

          case ColumnId.Pro:
            return (
              <ChampionProCell
                id={column.id}
                key={column.id}
                championPro={bookkeepingPendingAction.championPro}
              />
            )

          case ColumnId.ProEmail:
            return (
              <TextCell
                key={column.id}
                content={bookkeepingPendingAction.championPro?.email}
              />
            )

          case ColumnId.AccessMethod:
            return (
              <TextCell
                key={column.id}
                content={bookkeepingPendingAction.displayAccessMethod}
              />
            )

          case ColumnId.Action:
            return (
              <ActionCell
                key={column.id}
                content={bookkeepingPendingAction.displayAction}
              />
            )

          case ColumnId.TimeInAction:
            return (
              <TimeInActionCell
                key={column.id}
                time={bookkeepingPendingAction.datePendingSince}
              />
            )

          default:
            return <TableCell key={column.id} />
        }
      })}
    </TableRow>
  )
})

interface TextCellProps {
  content?: string
}

function TextCell({ content }: TextCellProps) {
  return (
    <TableCell className={styles.text}>
      <span>{content}</span>
    </TableCell>
  )
}

interface CompanyNameCellProps {
  platformConnection?: BookkeepingPendingActionPlatformConnection
}

function CompanyNameCell({ platformConnection }: CompanyNameCellProps) {
  return (
    <TableCell className={styles.text}>
      <span>{platformConnection?.displayName}</span>
    </TableCell>
  )
}

interface ImageCellProps {
  title?: string
  image?: string | null
}

function ImageCell({ title, image }: ImageCellProps) {
  return (
    <TableCell className={styles.text}>
      <ImageCellContent title={title} image={image} />
    </TableCell>
  )
}

interface ActionCellProps {
  content: string
}

function ActionCell({ content }: ActionCellProps) {
  return (
    <TableCell className={styles.text}>
      <Pill>{content}</Pill>
    </TableCell>
  )
}

interface TimeInActionCellProps {
  time: Date
}

function TimeInActionCell({ time }: TimeInActionCellProps) {
  const content = timeago(time, 'long')

  return <TextCell content={content} />
}

interface ChampionProCellProps {
  id: string
  championPro: Member
}

const ChampionProCell = observer(function ChampionProCell({
  id,
  championPro,
}: ChampionProCellProps) {
  if (championPro === Member.none) {
    return <TextCell content="" />
  }
  return (
    <ImageCell
      key={id}
      title={championPro.displayName}
      image={championPro.avatar}
    />
  )
})
